import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Table, Alert, Badge, Button } from 'react-bootstrap';
import { getAuthHeaders } from '../utils/authHelpers';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/OrderList.css';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const ORDERS_API_URL = `${API_ROOT_URL}/orders`;

const sortOrdersDescending = (a, b) => {
  const dateA = parseOrderDate(a.orderDate);
  const dateB = parseOrderDate(b.orderDate);
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return dateB.getTime() - dateA.getTime();
};

function getMonday(d) {
  const date = new Date(d);
  const day = date.getUTCDay();
  const diff = date.getUTCDate() - day + (day === 0 ? -6 : 1);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), diff));
}

function parseOrderDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

function formatWeekRange(date) {
  const monday = getMonday(date);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const startOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const endOptions = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
  return `${monday.toLocaleDateString('en-CA', startOptions)} - ${sunday.toLocaleDateString('en-CA', endOptions)}`;
}

function OrderList({
  onEdit,
  onDelete,
  searchTerm = '',
  filterStatus = 'All',
  filterOrderType = 'All',
  filterShipping = 'All',
  selectedWeek = 'all',
  setSelectedWeek,
  selectedMonth = 'all',
  setSelectedMonth,
  selectedYear = 'all',
  setSelectedYear
}) {
  const [allOrders, setAllOrders] = useState([]);
  const [displayedOrders, setDisplayedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weekOptions, setWeekOptions] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [copyStatus, setCopyStatus] = useState('default');
  const topScrollRef = useRef(null);
  const topInnerRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });

  const ROW_HEIGHT = 60;
  const BUFFER = 10;

  const formatDate = useCallback((dateLike) => {
    if (!dateLike) return 'N/A';
    const d = new Date(dateLike);
    return isNaN(d.getTime())
      ? 'N/A'
      : d.toLocaleDateString('en-CA', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
  }, []);

  const getStageBadgeVariant = useCallback((stage) => {
    switch (stage) {
      case 'Administration': return 'info';
      case 'Pre-Production': return 'primary';
      case 'Manufacturing': return 'warning';
      case 'Shipping': return 'success';
      case 'Completed': return 'dark';
      default: return 'secondary';
    }
  }, []);

  const handleCopyData = useCallback(() => {
    if (displayedOrders.length === 0) {
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('default'), 1500);
      return;
    }

    const headers = [
      'Order Type', 'Dealer Name', 'Customer Name', 'Revenue', 'Order Date',
      'Paid Date', 'Pre-Production Received', 'Manufacturing Received',
      'Build Started', 'Completion Date', 'Ship/Pickup Date',
    ];
    
    const dataRows = displayedOrders.map(order => {
      const paidDate = order?.administration?.paymentCompletedDate;
      const preProdRec = order?.administration?.completionDate;
      const manufacturingRec = order?.preProduction?.completionDate;
      const productionDate = order?.manufacturing?.orderEnteredProductionDate;
      const shipDate = order?.shipping?.shippedDate;
      
      const safeString = (value) => String(value || '').replace(/\t|\n|\r/g, ' ').replace(/,/g, '');
      const revenueValue = typeof order.revenue === 'number' ? order.revenue.toFixed(2) : 'N/A';

      return [
        safeString(order.orderType),
        safeString(order.dealerName),
        safeString(order.customerName),
        revenueValue,
        formatDate(order.orderDate),
        formatDate(paidDate),
        formatDate(preProdRec),
        formatDate(manufacturingRec),
        formatDate(productionDate),
        formatDate(order.completionDate),
        formatDate(shipDate),
      ].join('\t');
    });

    const tsvData = [headers.join('\t'), ...dataRows].join('\n');

    navigator.clipboard.writeText(tsvData)
      .then(() => {
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus('default'), 2000);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        setCopyStatus('error');
        setTimeout(() => setCopyStatus('default'), 2000);
      });
  }, [displayedOrders, formatDate]);

  const getButtonContent = () => {
    switch (copyStatus) {
      case 'copied': return `Copied!`;
      case 'error': return 'Failed to Copy';
      default: return `Copy ${displayedOrders.length} Orders`;
    }
  };

  const getButtonVariant = () => {
    switch (copyStatus) {
      case 'copied': return 'gray';
      case 'error': return 'danger';
      default: return 'dark';
    }
  };

  const fetchAllOrders = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(ORDERS_API_URL, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const sortedData = data.sort(sortOrdersDescending);
      setAllOrders(sortedData);

      const yearsSet = new Set();

      sortedData.forEach(order => {
        const od = parseOrderDate(order.orderDate);
        if (od) {
          yearsSet.add(od.getUTCFullYear());
        }
      });

      setYearOptions(Array.from(yearsSet).sort((a, b) => b - a));


    } catch (err) {
      setError(err.message || 'Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const monthMap = new Map();
    const weekMap = new Map();

    allOrders.forEach(order => {
      const d = parseOrderDate(order.orderDate);
      if (!d) return;

      const year = d.getUTCFullYear();
      const month = d.getUTCMonth();

      // Respect selected year filter
      if (selectedYear !== 'all' && year.toString() !== selectedYear) {
        return;
      }

      // Month key is year-month
      const monthKey = `${year}-${month}`;
      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, { year, month });
      }

      // Week options (respect month filter)
      if (
        selectedMonth === 'all' ||
        selectedMonth === monthKey
      ) {
        const monday = getMonday(d);
        const weekKey = monday.getTime();
        if (!weekMap.has(weekKey)) {
          weekMap.set(weekKey, formatWeekRange(monday));
        }
      }
    });

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const newMonthOptions = Array.from(monthMap.entries())
      .sort((a, b) => {
        const [aYear, aMonth] = a[0].split('-').map(Number);
        const [bYear, bMonth] = b[0].split('-').map(Number);
        return bYear - aYear || bMonth - aMonth;
      })
      .map(([key, { year, month }]) => ({
        key,
        label: `${monthNames[month]}, ${year}`
      }));

    const newWeekOptions = Array.from(weekMap.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([key, label]) => ({
        key: key.toString(),
        label
      }));

    setMonthOptions(newMonthOptions);
    setWeekOptions(newWeekOptions);
  }, [allOrders, selectedYear, selectedMonth]);

  useEffect(() => {
    setSelectedMonth('all');
    setSelectedWeek('all');
  }, [selectedYear, setSelectedMonth, setSelectedWeek]);

  useEffect(() => {
    setSelectedWeek('all');
  }, [selectedMonth, setSelectedWeek]);



  useEffect(() => {
    fetchAllOrders();
  }, [fetchAllOrders]);

  useEffect(() => {
    let filtered = allOrders;

    // Year Filter
    if (selectedYear !== 'all') {
      const yr = parseInt(selectedYear, 10);
      const start = new Date(Date.UTC(yr, 0, 1));
      const end = new Date(Date.UTC(yr + 1, 0, 1));
      filtered = filtered.filter(o => {
        const d = parseOrderDate(o.orderDate);
        return d && d >= start && d < end;
      });
    }

    // Month Filter
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(o => {
        const d = parseOrderDate(o.orderDate);
        if (!d) return false;
        const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
        return key === selectedMonth;
      });
    }

    // Week Filter
    if (selectedWeek && selectedWeek !== 'all') {
      const start = new Date(Number(selectedWeek));
      const end = new Date(start);
      end.setUTCDate(start.getUTCDate() + 7);
      filtered = filtered.filter(o => {
        const d = parseOrderDate(o.orderDate);
        return d && d >= start && d < end;
      });
    }

    // Production Stage Filter
    if (!filterStatus.includes('All') && Array.isArray(filterStatus)) {
      filtered = filtered.filter(o => filterStatus.includes(o.currentStage));
    }

    // Order Type Filter
    if (!filterOrderType.includes('All') && Array.isArray(filterOrderType)) {
      filtered = filtered.filter(o => filterOrderType.includes(o.orderType || ''));
    }

    // Shipping Method Filter
    if (!filterShipping.includes('All') && Array.isArray(filterShipping)) {
      filtered = filtered.filter(o => {
        const method = (o.shippingMethod || '').toLowerCase();
        return filterShipping.some(s => {
          const sel = s.toLowerCase();
          return sel === 'piggyback' ? method.startsWith('piggyback') : method === sel;
        });
      });
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(o => [
        o.orderId, o.dealerName, o.customerName, o.orderType, o.frameType, 
        o.upperColor, o.lowerColor, o.brackets, o.shippingMethod, 
        o.customWork, o.invoiceNumber, o.tbcNumber
      ].some(v => (v || '').toString().toLowerCase().includes(q)));
    }

    setDisplayedOrders(filtered);
  }, [allOrders, searchTerm, filterStatus, filterOrderType, filterShipping, selectedWeek, selectedMonth, selectedYear]);

  useEffect(() => {
    const topScroll = topScrollRef.current;
    const topInner = topInnerRef.current;
    const tableScroll = tableScrollRef.current;

    if (!topScroll || !topInner || !tableScroll) return;

    // ----- Set top scrollbar width -----
    topInner.style.width = `${tableScroll.scrollWidth}px`;

    const handleTopScroll = () => {
      tableScroll.scrollLeft = topScroll.scrollLeft;
    };

    const handleTableScroll = () => {
      // ----- Sync horizontal scroll -----
      topScroll.scrollLeft = tableScroll.scrollLeft;

      // ----- Virtualization (vertical) -----
      const scrollTop = tableScroll.scrollTop;
      const containerHeight = tableScroll.clientHeight;

      const start = Math.floor(scrollTop / ROW_HEIGHT);
      const visibleCount = Math.ceil(containerHeight / ROW_HEIGHT);

      const newStart = Math.max(0, start - BUFFER);
      const newEnd = Math.min(
        displayedOrders.length,
        start + visibleCount + BUFFER
      );

      setVisibleRange(prev =>
        prev.start !== newStart || prev.end !== newEnd
          ? { start: newStart, end: newEnd }
          : prev
      );
    };

    topScroll.addEventListener('scroll', handleTopScroll);
    tableScroll.addEventListener('scroll', handleTableScroll);

    // Initial calculation
    handleTableScroll();

    const resizeObserver = new ResizeObserver(() => {
      topInner.style.width = `${tableScroll.scrollWidth}px`;
    });

    resizeObserver.observe(tableScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableScroll.removeEventListener('scroll', handleTableScroll);
      resizeObserver.disconnect();
    };
  }, [displayedOrders]);

  if (loading && allOrders.length === 0) {
    return (
      <Container className="my-5 text-center">
        <p className="mt-2">Loading orders...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Orders</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="my-5">
      <Row className="justify-content-md-center">
        <Col md={12}>
          <div className="list-container">
            <Row className="mb-3 align-items-center filter-bar">
              <Col md="auto" className="d-flex align-items-center">
                <label className="me-2 fw-bold mb-0">Year:</label>
                <select className="form-select" style={{ width: '140px' }} value={selectedYear ?? 'all'} onChange={(e) => setSelectedYear(e.target.value)}>
                  <option value="all">All Years</option>
                  {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
                </select>
              </Col>

              <Col md="auto" className="d-flex align-items-center ms-3">
                <label className="me-2 fw-bold mb-0">Month:</label>
                <select className="form-select" style={{ width: '140px' }} value={selectedMonth ?? 'all'} onChange={(e) => setSelectedMonth(e.target.value)}>
                  <option value="all">All Months</option>
                  {monthOptions.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </Col>

              <Col md="auto" className="d-flex align-items-center ms-3">
                <label className="me-2 fw-bold mb-0">Week:</label>
                <select className="form-select" style={{ width: '160px' }} value={selectedWeek ?? 'all'} onChange={(e) => setSelectedWeek(e.target.value)}>
                  <option value="all">All Weeks</option>
                  {weekOptions.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
                </select>
              </Col>

              <Col md="auto" className="ms-auto">
                <Button variant={getButtonVariant()} onClick={handleCopyData} disabled={copyStatus !== 'default' || displayedOrders.length === 0}>
                  {getButtonContent()}
                </Button>
              </Col>
            </Row>

            {displayedOrders.length === 0 ? (
              <Alert variant="info" className="text-center">No orders found matching your criteria.</Alert>
            ) : (
              <div className='table-scroll-top-wrapper'>
                <div className='top-scroll' ref={topScrollRef}>
                  <div className='top-scroll-inner' ref={topInnerRef}></div>
                </div>
                <div className="table-responsive" ref={tableScrollRef}>
                  <Table bordered hover className="shadow-sm rounded order-list-table" style={{ minWidth: '2000px', tableLayout: 'fixed' }}>
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: '100px' }}>Order ID</th>
                        <th style={{ width: '80px' }}>Invoice Number</th>
                        <th style={{ width: '80px' }}>TBC Number</th>
                        <th style={{ width: '100px' }}>Order Type</th>
                        <th style={{ width: '120px' }}>Production Stage</th>
                        <th style={{ width: '130px' }}>Dealer Name</th>
                        <th style={{ width: '130px' }}>Customer Name</th>
                        <th style={{ width: '180px' }}>Custom Work</th>
                        <th style={{ width: '70px' }}>Length</th>
                        <th style={{ width: '70px' }}>Width</th>
                        <th style={{ width: '70px' }}>Height</th>
                        <th style={{ width: '100px' }}>Frame</th>
                        <th style={{ width: '100px' }}>Upper Color</th>
                        <th style={{ width: '100px' }}>Lower Color</th>
                        <th style={{ width: '120px' }}>Brackets</th>
                        <th style={{ width: '100px' }}>Revenue</th>
                        <th style={{ width: '100px' }}>Shipping Method</th>
                        <th style={{ width: '100px' }}>Order Date</th>
                        <th style={{ width: '100px' }}>Paid Date</th>
                        <th style={{ width: '125px' }}>PreProduction Received</th>
                        <th style={{ width: '125px' }}>Manufacturing Received</th>
                        <th style={{ width: '100px' }}>Build Started</th>
                        <th style={{ width: '110px' }}>Completion Date</th>
                        <th style={{ width: '110px' }}>Ship/Pickup Date</th>
                        <th style={{ width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Top Spacer */}
                      <tr>
                        <td colSpan="25" style={{height: visibleRange.start * ROW_HEIGHT, padding: 0, border: 0 }}/>
                      </tr>

                      {displayedOrders.slice(visibleRange.start, visibleRange.end).map(order => (
                        <tr key={order.orderId}>
                          <td>{order.orderId}</td>
                          <td>{order.invoiceNumber || 'N/A'}</td>
                          <td>{order.tbcNumber || 'N/A'}</td>
                          <td>{order.orderType || 'N/A'}</td>
                          <td><Badge bg={getStageBadgeVariant(order.currentStage)}>{order.currentStage}</Badge></td>
                          <td>{order.dealerName || 'N/A'}</td>
                          <td>{order.customerName || 'N/A'}</td>
                          <td>{order.customWork || 'None'}</td>
                          <td>{order.length ?? 'N/A'}</td>
                          <td>{order.width ?? 'N/A'}</td>
                          <td>{order.height ?? 'N/A'}</td>
                          <td>{order.frameType || 'N/A'}</td>
                          <td>{order.upperColor || 'N/A'}</td>
                          <td>{order.lowerColor || 'N/A'}</td>
                          <td>{order.brackets || 'N/A'}</td>
                          <td>{typeof order.revenue === 'number' ? `$${order.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                          <td>{order.shippingMethod || 'N/A'}</td>
                          <td>{formatDate(order.orderDate)}</td>
                          <td>{formatDate(order?.administration?.paymentCompletedDate)}</td>
                          <td>{formatDate(order?.administration?.completionDate)}</td>
                          <td>{formatDate(order?.preProduction?.completionDate)}</td>
                          <td>{formatDate(order?.manufacturing?.orderEnteredProductionDate)}</td>
                          <td>{formatDate(order.completionDate)}</td>
                          <td>{formatDate(order?.shipping?.shippedDate)}</td>
                          <td>
                            <Button variant="warning" size="sm" className="me-2" onClick={() => onEdit(order)}>Edit</Button>
                            <Button variant="danger" size="sm" onClick={() => onDelete(order)}>Delete</Button>
                          </td>
                        </tr>
                      ))}
                      {/* Bottom Spacer */}
                      <tr>
                        <td colSpan="25" style={{height: (displayedOrders.length - visibleRange.end) * ROW_HEIGHT, padding: 0, border: 0 }}/>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default OrderList;